import MarkdownIt from 'markdown-it'
import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify'
import { configureStoryDialogue } from '@/utils/storyDialogue'

export const DESCRIPTION_MARKDOWN_POLICY = {
  storage: 'markdown',
  supported: [
    'paragraphs',
    'headings',
    'bold',
    'italic',
    'lists',
    'blockquotes',
    'links',
    'horizontal_rules',
    'url_images',
  ],
  unsupported: [
    'tables',
    'math',
    'code_blocks',
    'inline_code',
    'alignment_syntax',
    'uploaded_or_local_images',
  ],
} as const

const STORYTELLING_ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 's', 'del', 'ins',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'code', 'pre',
  'hr', 'a',
]

const STORYTELLING_ALLOWED_ATTR = [
  'href',
  'title',
  'rel',
]

const DESCRIPTION_ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote',
  'hr', 'a', 'img',
]

const DESCRIPTION_ALLOWED_ATTR = [
  'href',
  'src',
  'alt',
  'title',
  'rel',
]

const MINIMAL_INLINE_ALLOWED_TAGS = [
  'br', 'strong', 'em', 'a',
]

export type DescriptionRenderMode = 'full' | 'compact'

const SAFE_URL_PATTERN = /^(https?:)?\/\//i
const UNSAFE_MARKDOWN_IMAGE_PATTERN = /!\[([^\]]*)\]\((?:blob:|file:|data:)[^)]+\)/gi
const MARKDOWN_IMAGE_PATTERN = /!\[([^\]]*)\]\(([^)]+)\)/g
const COMPACT_DESCRIPTION_TEXT_LIMIT = 360

export const md = new MarkdownIt('commonmark', {
  html: false,
  linkify: false,
  typographer: true,
  breaks: false,
})

export const mdMinimal = new MarkdownIt('commonmark', {
  html: false,
  linkify: false,
  typographer: true,
  breaks: true,
})

configureStoryDialogue(md)
configureStoryDialogue(mdMinimal)

function isSafeDescriptionUrl(url: string): boolean {
  return SAFE_URL_PATTERN.test(String(url || '').trim())
}

function configureDescriptionRenderer(renderer: MarkdownIt): MarkdownIt {
  renderer.validateLink = (url) => isSafeDescriptionUrl(url)
  renderer.renderer.rules.image = (tokens, idx, options, env, self) => {
    const token = tokens[idx]
    const src = token.attrGet('src') || ''
    if (!isSafeDescriptionUrl(src)) return ''
    const title = token.attrGet('title')
    const attrs = [
      `src="${renderer.utils.escapeHtml(src)}"`,
      `alt="${renderer.utils.escapeHtml(token.content)}"`,
      title ? `title="${renderer.utils.escapeHtml(title)}"` : '',
    ].filter(Boolean)
    return `<img ${attrs.join(' ')}>`
  }
  renderer.renderer.rules.code_inline = (tokens, idx) => renderer.utils.escapeHtml(tokens[idx].content)
  renderer.renderer.rules.code_block = (tokens, idx) => `<p>${renderer.utils.escapeHtml(tokens[idx].content)}</p>\n`
  renderer.renderer.rules.fence = (tokens, idx) => `<p>${renderer.utils.escapeHtml(tokens[idx].content)}</p>\n`
  return renderer
}

const mdDescription = configureDescriptionRenderer(new MarkdownIt('commonmark', {
  html: false,
  linkify: false,
  typographer: true,
  breaks: false,
}))

const mdDescriptionCompact = configureDescriptionRenderer(new MarkdownIt('commonmark', {
  html: false,
  linkify: false,
  typographer: true,
  breaks: true,
}))

interface Segment {
  type: 'text' | 'fence'
  lines: string[]
}

function normalizeMarkdown(markdown: string): string {
  if (!markdown) return ''

  const lines = String(markdown).replace(/\r\n?/g, '\n').split('\n')
  const segments: Segment[] = []
  let insideFence = false
  let fenceChar = ''
  let fenceLen = 0
  let current: string[] = []

  for (const line of lines) {
    if (!insideFence) {
      const m = /^(`{3,}|~{3,})/.exec(line)
      if (m) {
        if (current.length) segments.push({ type: 'text', lines: current })
        current = [line]
        insideFence = true
        fenceChar = m[1][0]
        fenceLen = m[1].length
      } else {
        current.push(line)
      }
    } else {
      current.push(line)
      const trimmed = line.trimEnd()
      if (
        trimmed.length >= fenceLen
        && trimmed === fenceChar.repeat(trimmed.length)
        && trimmed[0] === fenceChar
      ) {
        segments.push({ type: 'fence', lines: current })
        current = []
        insideFence = false
      }
    }
  }

  if (current.length) segments.push({ type: 'text', lines: current })

  return segments
    .map((seg) => {
      if (seg.type === 'fence') return seg.lines.join('\n')
      return seg.lines.join('\n')
        .replace(/[ \t]+$/gm, '')
        .replace(/\n(?!\n)/g, '\n\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/^>(?! )/gm, '> ')
    })
    .join('\n')
    .trim()
}

function sanitizeMarkdownHtml(rawHtml: string, purifyConfig: DOMPurifyConfig = {}, dialogue = false): string {
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: dialogue ? [...STORYTELLING_ALLOWED_TAGS, 'span'] : STORYTELLING_ALLOWED_TAGS,
    ALLOWED_ATTR: dialogue ? [...STORYTELLING_ALLOWED_ATTR, 'class'] : STORYTELLING_ALLOWED_ATTR,
    ADD_ATTR: ['target'],
    ALLOW_DATA_ATTR: false,
    ...purifyConfig,
  }) as string
}

function sanitizeDescriptionMarkdownHtml(rawHtml: string, purifyConfig: DOMPurifyConfig = {}): string {
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: DESCRIPTION_ALLOWED_TAGS,
    ALLOWED_ATTR: DESCRIPTION_ALLOWED_ATTR,
    ADD_ATTR: ['target'],
    ALLOW_DATA_ATTR: false,
    ...purifyConfig,
  }) as string
}

export interface ParseMarkdownOptions {
  minimal?: boolean
  dialogue?: boolean
}

/**
 * Converts markdown to sanitized HTML
 */
export function parseMarkdown(markdown: string, purifyConfig: Record<string, unknown> = {}, options: ParseMarkdownOptions = {}): string {
  if (!markdown) return ''

  const { minimal = false, dialogue = false } = options
  const normalized = minimal
    ? String(markdown).replace(/\r\n?/g, '\n').trim()
    : normalizeMarkdown(markdown)
  const renderer = minimal ? mdMinimal : md
  const rootClassName = minimal ? 'markdown markdown--minimal' : 'markdown'
  const rawHtml = renderer.render(normalized, { storyDialogue: dialogue })
  const cleanHtml = sanitizeMarkdownHtml(rawHtml, purifyConfig, dialogue)

  return `<div class="${rootClassName}">${cleanHtml}</div>`
}

export function parseMinimalMarkdownInline(markdown: string, purifyConfig: DOMPurifyConfig = {}): string {
  if (!markdown) return ''

  const normalized = String(markdown).replace(/\r\n?/g, '\n').trim()
  if (!normalized) return ''

  const rawHtml = normalized
    .split('\n')
    .map((line) => {
      const headingMatch = /^(#{1,6})[ \t]+(.+)$/.exec(line.trim())
      if (headingMatch) {
        return `<strong>${mdMinimal.renderInline(headingMatch[2])}</strong>`
      }
      return mdMinimal.renderInline(line)
    })
    .join('<br />')

  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: MINIMAL_INLINE_ALLOWED_TAGS,
    ALLOWED_ATTR: STORYTELLING_ALLOWED_ATTR,
    ADD_ATTR: ['target'],
    ALLOW_DATA_ATTR: false,
    ...purifyConfig,
  }) as string
}

export function sanitizeDescriptionMarkdownForPersistence(markdown: string): string {
  if (!markdown) return ''

  return String(markdown)
    .replace(UNSAFE_MARKDOWN_IMAGE_PATTERN, (_match, alt) => String(alt || '').trim())
    .trim()
}

function truncateText(text: string, limit: number): string {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (normalized.length <= limit) return normalized
  return `${normalized.slice(0, Math.max(0, limit - 1)).trimEnd()}...`
}

function truncateCompactMarkdown(markdown: string, limit: number): string {
  const normalized = markdown.trim()
  if (normalized.length <= limit) return normalized

  const strongMatch = /^\*\*([\s\S]+)\*\*$/.exec(normalized)
  if (strongMatch) return `**${truncateText(strongMatch[1], limit - 4)}**`

  const emphasisMatch = /^\*([\s\S]+)\*$/.exec(normalized)
  if (emphasisMatch) return `*${truncateText(emphasisMatch[1], limit - 2)}*`

  return truncateText(normalized, limit)
}

function compactDescriptionMarkdown(markdown: string): string {
  const lines = String(markdown)
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      if (/^!\[[^\]]*]\([^)]+\)$/.test(line)) return ''
      return line
        .replace(/^#{1,6}[ \t]+/, '')
        .replace(/^>[ \t]?/, '')
        .replace(/^[-*+][ \t]+/, '')
        .replace(/^\d+[.)][ \t]+/, '')
        .replace(MARKDOWN_IMAGE_PATTERN, '')
    })
    .filter(Boolean)

  return truncateCompactMarkdown(lines.join('\n'), COMPACT_DESCRIPTION_TEXT_LIMIT)
}

export function parseDescriptionMarkdown(
  markdown: string,
  mode: DescriptionRenderMode = 'full',
  purifyConfig: DOMPurifyConfig = {},
): string {
  if (!markdown) return ''

  if (mode === 'compact') {
    const compactMarkdown = compactDescriptionMarkdown(markdown)
    if (!compactMarkdown) return ''

    const rawHtml = compactMarkdown
      .split('\n')
      .map((line) => mdDescriptionCompact.renderInline(line))
      .join('<br />')

    const cleanHtml = sanitizeDescriptionMarkdownHtml(rawHtml, purifyConfig)
    return `<div class="markdown markdown--description markdown--description-compact">${cleanHtml}</div>`
  }

  const normalized = normalizeMarkdown(sanitizeDescriptionMarkdownForPersistence(markdown))
  const rawHtml = mdDescription.render(normalized)
  const cleanHtml = sanitizeDescriptionMarkdownHtml(rawHtml, {
    ...purifyConfig,
    ADD_ATTR: ['target'],
  })

  return `<div class="markdown markdown--description markdown--description-full">${cleanHtml}</div>`
}
